import { resolveCachedItemForRender, syncCachedInventoryContents, nodeHasStatusItemId, LOCKED_STATUS_ITEM_ID } from './find-cached-item';
import { inventoryField, itemsCol } from '../shared/array-types';
import { decodeHtmlEntitiesForPlainText } from '../shared/decode-html-entities';
import { isContainerArr, renderContainerAccess, selectCardPictureUrl } from './render-object-card';
import { islandPlaceholder, ftHydrateObjectCardIslands } from './islands';
import { takeAllCapacityReason } from './take-all-capacity';
import { corpseTakeAllRows } from './corpse-body';

type Row = Record<string, unknown>;
type Destination = { id: string; name: string };
type LootState = { destination: string; busy: boolean; message: string };
const states = new WeakMap<HTMLElement, LootState>();
const lastHtml = new WeakMap<HTMLElement, string>();
const host = window as Window & {
    ftCreateWorldContainerWindow?: (id: string, title: string) => HTMLElement | null;
    ftPrepareLootWindows?: (casterId: string) => void;
    ftLootDestinations?: (casterId: string) => Destination[];
    ftArrangeLootWindows?: (id: string, casterId: string) => void;
    ftGetActiveCasterId?: () => string;
    fetchAndCacheObject?: (id: string) => Promise<unknown>;
    fetchAndCacheCaster?: (id: string, options: { skipEncounterSeed: boolean }) => Promise<void>;
    ftRerenderPcSheet?: (id: string) => boolean;
    refreshDragAndDrop?: () => void;
    displayWarning?: (message: string) => void;
    ftTakeAllInventoryWithReceipt?: (source: string, destination: string,
        entries: { inventory_id: string; item_qty: number }[]) => Promise<number>;
};
const text = (value: unknown): string => decodeHtmlEntitiesForPlainText(String(value ?? ''));
const nameOf = (row: Row): string => text(inventoryField(row, 'inventory_name') || itemsCol(row, 'item_name') || 'Container');
const actorId = (): string => String(host.ftGetActiveCasterId?.() ?? '').toUpperCase();
const locked = (row: Row): boolean => nodeHasStatusItemId(row, 'target', LOCKED_STATUS_ITEM_ID) || nodeHasStatusItemId(row, 'caster', LOCKED_STATUS_ITEM_ID);
function contents(row: Row): Row[] {
    const entries = row.inventory_in_container;
    const own = (Array.isArray(entries) ? entries : entries ? [entries] : []).filter((entry): entry is Row => !!entry && typeof entry === 'object');
    // A corpse also yields what its creature still has on it (worn only out of combat — see corpse-body.ts).
    return [...own, ...corpseTakeAllRows(row)];
}
function refusal(row: Row | null): string {
    if (!row?.permissions || !isContainerArr(row) || Number(inventoryField(row, 'inventory_deleted')) === 1) return 'This container is no longer available.';
    if (!actorId()) return 'Control a character to take inventory.';
    if (locked(row)) return 'Unlock the container first.';
    if (window.ftActiveViewerCanReach?.(row) !== true) return 'Move closer to take inventory.';
    if (!contents(row).length) return 'This container is empty.';
    return '';
}

function capacityReason(row: Row, destination: string): string {
    const actor = actorId();
    const caster = window.FateTide?.casterCache?.get(actor) ?? null;
    return takeAllCapacityReason(caster, contents(row), destination, actor, resolveCachedItemForRender);
}

function paint(id: string, body: HTMLElement): void {
    const win = body.closest<HTMLElement>('[data-ft-world-container]');
    if (!win) return;
    const state = states.get(win) ?? { destination: '', busy: false, message: '' };
    states.set(win, state);
    const row = resolveCachedItemForRender(id);
    const available = !!row?.permissions && isContainerArr(row) && Number(inventoryField(row, 'inventory_deleted')) !== 1;
    const fragment = document.createElement('div');
    fragment.innerHTML = !available ? '<p class="p-3">This container is no longer available.</p>'
        : locked(row) ? '<p class="p-3">This container is locked.</p>' + islandPlaceholder('lock_controls', id)
        : renderContainerAccess(row);
    const banner = fragment.querySelector('.bg-info');
    const sorting = banner?.querySelector('.btn-group');
    const sortHtml = sorting?.outerHTML ?? '';
    sorting?.remove();
    const stats = banner?.querySelector('.justify-content-end')?.innerHTML ?? '';
    banner?.remove();
    const grid = fragment.querySelector<HTMLElement>('#div_caster_view_container_inventory');
    if (grid) { grid.removeAttribute('id'); grid.dataset.ftContainerGrid = ''; }
    const bodyHtml = (sortHtml ? `<div class="ft-loot__sort">${sortHtml}</div>` : '') + fragment.innerHTML;
    if (lastHtml.get(body) !== bodyHtml) {
        lastHtml.set(body, bodyHtml);
        body.innerHTML = bodyHtml;
        window.ftMountInventoryTiles?.(body);
        window.ftMountIcons?.(body);
        if (available) ftHydrateObjectCardIslands(body, row);
        host.refreshDragAndDrop?.();
    }
    const destinations = host.ftLootDestinations?.(actorId()) ?? [];
    if (!destinations.some(destination => destination.id === state.destination)) state.destination = destinations[0]?.id ?? '';
    const reason = refusal(row) || (!state.destination ? 'Open your character’s inventory first.' : row ? capacityReason(row, state.destination) : '');
    const title = win.querySelector<HTMLElement>('.ft-bagwin__title');
    if (!title) return;
    const thumbnail = row ? selectCardPictureUrl(row) : '';
    const type = row ? String(itemsCol(row, 'item_type') || 'container') : 'container';
    const html = `<div data-ft-loot-identity class="ft-loot__identity">`
        + `<span class="ft-loot__thumbnail">${thumbnail ? `<img src="${escapeHtml(thumbnail)}" alt="" width="64" height="64">` : iconMap(type, 40, 40)}</span>`
        + `<div class="ft-loot__name"><strong>${escapeHtml(row ? nameOf(row) : 'Container')}</strong>`
        + `<span class="ft-loot__type">${iconMap(type, 16, 16)} ${escapeHtml(ucwordsAndSpace(type))}</span>`
        + `<div class="ft-loot__stats">${stats}</div></div></div>`
        + `<div class="ft-loot__actions"><label><select data-ft-loot-destination aria-label="Take all destination" ${state.busy ? 'disabled' : ''}>`
        + (destinations.length ? destinations.map(destination => `<option value="${escapeHtml(destination.id)}" ${destination.id === state.destination ? 'selected' : ''}>${escapeHtml(destination.name)}</option>`).join('') : '<option value="">No controlled character</option>')
        + `</select></label><button type="button" class="btn btn-info btn-sm" data-ft-take-all ${state.busy || reason ? 'disabled' : ''} title="${escapeHtml(reason || 'Take every stack into the selected destination')}">`
        + `${iconMap('pick_up_inventory', 19, 19)} ${state.busy ? 'Taking…' : 'Take all'}</button></div>`
        + `<div class="ft-loot__message" role="status" aria-live="polite">${escapeHtml(state.message || (reason === 'This container is empty.' ? '' : reason))}</div>`;
    if (lastHtml.get(title) !== html) { lastHtml.set(title, html); title.innerHTML = html; window.ftMountIcons?.(title); }
    if (!win.dataset.ftLootWired) {
        win.dataset.ftLootWired = '1';
        win.addEventListener('change', event => {
            if (event.target instanceof HTMLSelectElement && event.target.matches('[data-ft-loot-destination]')) { state.destination = event.target.value; state.message = ''; paint(id, body); }
        });
        win.addEventListener('click', event => {
            if ((event.target as Element)?.closest('[data-ft-take-all]')) void takeAll(id, body, state);
        });
    }
}

async function takeAll(id: string, body: HTMLElement, state: LootState): Promise<void> {
    if (state.busy) return;
    const actor = actorId(), destination = state.destination;
    const row = resolveCachedItemForRender(id);
    const reason = refusal(row) || (row ? capacityReason(row, destination) : '');
    if (reason || !row || !host.ftTakeAllInventoryWithReceipt) { state.message = reason || 'Inventory transfer is unavailable.'; paint(id, body); return; }
    const entries = contents(row).map(entry => ({ inventory_id: String(inventoryField(entry, 'inventory_id') ?? '').toUpperCase(), item_qty: Number(inventoryField(entry, 'item_qty') ?? 1) }));
    if (!entries.length || entries.some(entry => !entry.inventory_id || !Number.isSafeInteger(entry.item_qty) || entry.item_qty < 1)) {
        state.message = 'Inventory changed. Reopen the container.'; paint(id, body); return;
    }
    state.busy = true; state.message = ''; paint(id, body);
    let completed = 0;
    try {
        completed = await host.ftTakeAllInventoryWithReceipt(id, destination, entries);
        if (completed) {
            // Several commits can have older socket snapshots still in flight. Refresh both sides
            // once after the batch, including computed weight/value (receipts only move rows).
            await host.fetchAndCacheCaster?.(actor, { skipEncounterSeed: true });
            // The caster fetch may join an older in-flight request. Fetch each container afterward
            // and synchronize every nested alias, so a bag cannot keep that request's older contents.
            const containers = destination === actor ? [id] : [id, destination];
            const fresh = await Promise.all(containers.map(containerId => host.fetchAndCacheObject?.(containerId)));
            fresh.forEach((blob, index) => { if (blob) syncCachedInventoryContents(blob, containers[index]!); });
            host.ftRerenderPcSheet?.(actor);
        }
        if (completed === entries.length) state.message = `Took all ${completed} ${completed === 1 ? 'stack' : 'stacks'}.`;
        else if (completed) state.message = `Took ${completed} ${completed === 1 ? 'stack' : 'stacks'}. ${state.message}`;
    } catch (error) {
        console.error('Take all failed', error);
        state.message = 'Transfer could not be confirmed. Review the remaining inventory before trying again.';
        await host.fetchAndCacheCaster?.(actor, { skipEncounterSeed: true });
        const fresh = await host.fetchAndCacheObject?.(id);
        if (fresh) syncCachedInventoryContents(fresh, id);
    } finally { state.busy = false; if (body.isConnected) paint(id, body); }
}

async function openWorldContainer(id: string): Promise<void> {
    id = id.trim().toUpperCase();
    if (!id) return;
    const actor = actorId();
    try {
        let row = resolveCachedItemForRender(id);
        if (!row?.permissions) { await host.fetchAndCacheObject?.(id); row = resolveCachedItemForRender(id); }
        if (actorId() !== actor) return;
        if (!row?.permissions || !isContainerArr(row)) { host.displayWarning?.('This container is not available.'); return; }
        host.ftPrepareLootWindows?.(actor);
        const body = host.ftCreateWorldContainerWindow?.(id, nameOf(row));
        if (body) {
            paint(id, body);
            window.requestAnimationFrame(() => { if (body.isConnected) host.ftArrangeLootWindows?.(id, actor); });
        }
    } catch (error) { console.error('Open container failed', error); host.displayWarning?.('Could not open this container. Please try again.'); }
}

/** FT3D "Open / loot" approach gate: can the controlled character reach this container now? A cold row is
 *  fetched first. null = undecidable (no container row / reach unresolved) — the caller just opens. */
async function worldContainerReach(id: string): Promise<boolean | null> {
    let row = resolveCachedItemForRender(id);
    if (!row?.permissions) { await host.fetchAndCacheObject?.(id); row = resolveCachedItemForRender(id); }
    if (!row?.permissions || !isContainerArr(row)) return null;
    return typeof window.ftActiveViewerCanReach === 'function' ? window.ftActiveViewerCanReach(row) : true;
}

function refreshWorldContainers(): void {
    document.querySelectorAll<HTMLElement>('[data-ft-world-container]').forEach(win => {
        const body = win.querySelector<HTMLElement>('[data-ft-world-container-body]');
        if (body) paint(win.dataset.ftWorldContainer ?? '', body);
    });
}
Object.assign(window, { ftOpenWorldContainer: openWorldContainer, ftRefreshWorldContainers: refreshWorldContainers,
    ftWorldContainerReach: worldContainerReach });
